// 标签式列表（Focus Carousel）数据标准化：完全按当前 rootMenu 及其子菜单驱动
import { resolveFileUrl } from '@/components/shared/utils/fileUrl'
import { resolveBodyHtml } from '@/components/shared/utils/resolveBodyHtml'
import { normalizeContentAttachments } from '@/components/shared/utils/contentAttachments'
import { parseShowAttachments } from '@/components/shared/utils/contentDisplayFlags'
import { extractFirstContentImage } from '@/components/home/utils/extractFirstContentImage'
import {
  getMenuNodePath,
  slugFromMenuPath
} from '@/components/portal/common/utils/menuPath'
import { isPortalMenuVisible } from '@/components/portal/common/utils/portalMenuVisibility'

/** 格式化发布日期为 YYYY-MM-DD（本地时区；不回退 updatedAt） */
function formatPublishedAt(content) {
  const raw = content?.publishedAt || content?.publishTime || content?.createdAt
  if (!raw) return ''
  const date = new Date(raw)
  if (Number.isNaN(date.getTime())) return String(raw).slice(0, 10)
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** 新闻列表排序：先按发布日期降序，再按 sortOrder */
function sortNewsItems(a, b) {
  const dateCompare = (b.publishedAt || '').localeCompare(a.publishedAt || '')
  if (dateCompare !== 0) return dateCompare
  return a.sortOrder - b.sortOrder
}

function resolveTabSlug(item) {
  return slugFromMenuPath(item.path) || String(item.id)
}

/** 按当前门户 rootMenu 构建顶部分类导航 */
export function buildPortalFocusNavItems(menus = [], rootMenu = null) {
  if (!rootMenu) return []

  const seen = new Set()
  const items = []

  for (const item of (rootMenu.children || [])
    .filter((child) => isPortalMenuVisible(child))
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))) {
    const path = getMenuNodePath(item)
    if (!path) continue

    const slug = resolveTabSlug(item)
    if (seen.has(slug)) continue
    seen.add(slug)

    items.push({
      menuId: item.id,
      slug,
      name: item.name,
      path,
      contentType: item.contentType || 'NEWS',
      sortOrder: item.sortOrder ?? 0
    })
  }

  return items
}

/** 递归收集子树叶子节点的内容拉取目标 */
function collectLeafFetchTargets(nodes = [], parentCategory = '', addTarget) {
  for (const node of nodes) {
    if (!isPortalMenuVisible(node)) continue

    const category = resolveTabSlug(node) || parentCategory
    const children = (node.children || []).filter((item) => isPortalMenuVisible(item))

    if (!children.length) {
      addTarget({
        menuId: node.id,
        category: category || String(node.id),
        contentType: node.contentType || 'NEWS',
        sortOrder: node.sortOrder ?? 0
      })
      continue
    }

    collectLeafFetchTargets(children, category, addTarget)
  }
}

/** 根据当前 rootMenu 生成内容拉取目标 */
export function buildContentFetchTargets(menus = [], rootMenu = null) {
  if (!rootMenu) return []

  const targets = []
  const seen = new Set()

  const addTarget = (target) => {
    if (!target.menuId || seen.has(target.menuId)) return
    seen.add(target.menuId)
    targets.push(target)
  }

  for (const child of (rootMenu.children || []).filter((item) => isPortalMenuVisible(item))) {
    const category = resolveTabSlug(child)
    const nested = (child.children || []).filter((item) => isPortalMenuVisible(item))

    if (!nested.length) {
      addTarget({
        menuId: child.id,
        category: category || String(child.id),
        contentType: child.contentType || 'NEWS',
        sortOrder: child.sortOrder ?? 0
      })
      continue
    }

    collectLeafFetchTargets(nested, category, addTarget)
  }

  return targets
}

/** 将原始内容列表标准化为统一结构（用于批量拉取） */
export function normalizeContentList(contents = [], meta = {}) {
  return (contents || [])
    .map((content) => ({
      id: content.id,
      menuId: content.menuId,
      slug: String(content.id),
      category: meta.category || '',
      title: content.title || '',
      summary: content.summary || '',
      cover: resolveFileUrl(content.coverUrl)
        || extractFirstContentImage(content.bodyHtml, content.bodyJson),
      publishedAt: formatPublishedAt(content),
      sortOrder: content.sortOrder ?? meta.sortOrder ?? 0
    }))
    .sort(sortNewsItems)
}

/** 按 category 过滤内容拉取目标 */
export function filterFetchTargets(targets = [], payload = {}) {
  const { category } = payload
  if (category) return targets.filter((target) => target.category === category)
  return targets
}

/** 标准化新闻/投资者详情数据 */
export function normalizeNewsDetail(content, listItem = null) {
  if (!content) return null

  return {
    id: content.id,
    menuId: content.menuId,
    slug: String(content.id),
    category: listItem?.category || '',
    title: content.title || listItem?.title || '',
    summary: content.summary || listItem?.summary || '',
    cover: resolveFileUrl(content.coverUrl)
      || listItem?.cover
      || extractFirstContentImage(content.bodyHtml, content.bodyJson),
    content: resolveBodyHtml(content.bodyHtml, content.bodyJson),
    attachments: normalizeContentAttachments(content.attachments),
    showAttachments: parseShowAttachments(content),
    publishedAt: formatPublishedAt(content) || listItem?.publishedAt || ''
  }
}
